import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";

/* ─── Types ──────────────────────────────────────────────────── */
type MediaPkg = {
  id: string;
  tenant_id: string;
  package_type: string;
  title: string;
  cover_url: string | null;
  frame_url: string | null;
  video_url: string | null;
  download_url: string | null;
  usb_label: string | null;
  notes: string | null;
  is_published: boolean;
  view_count: number;
  download_count: number;
};

type PkgFile = {
  id: string;
  file_type: string;
  title: string;
  url: string;
  thumbnail_url: string | null;
  sort_order: number;
};

/* ─── Video embed helper ─────────────────────────────────────── */
function getEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") || u.hostname.includes("youtu.be")) {
      const id = u.hostname.includes("youtu.be")
        ? u.pathname.slice(1)
        : u.searchParams.get("v") ?? u.pathname.split("/").pop();
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    return url;
  } catch {
    return null;
  }
}

function isEmbeddable(url: string): boolean {
  return url.includes("youtube.com") || url.includes("youtu.be") || url.includes("vimeo.com");
}

/* ─── Loading ────────────────────────────────────────────────── */
function MediaLoading() {
  return (
    <div className="min-h-screen bg-[#0a0a0e] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

/* ─── Not found ──────────────────────────────────────────────── */
function MediaNotFound() {
  return (
    <div className="min-h-screen bg-[#0a0a0e] flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-white/5 text-white/20">
          <i className="ri-disc-line text-3xl"></i>
        </div>
        <h1 className="text-white text-xl font-semibold mb-2">Package not found</h1>
        <p className="text-white/30 text-sm">This media package may have been removed or is not yet published. Contact your studio for access.</p>
      </div>
    </div>
  );
}

const TYPE_INFO: Record<string, { label: string; icon: string; color: string }> = {
  online_dvd:  { label: "Online DVD",    icon: "ri-disc-line",        color: "#a78bfa" },
  usb:         { label: "USB Package",   icon: "ri-usb-line",         color: "#fbbf24" },
  embed:       { label: "Embed Player",  icon: "ri-play-circle-line", color: "#38bdf8" },
  cover_frame: { label: "Cover / Frame", icon: "ri-image-edit-line",  color: "#34d399" },
  combo:       { label: "Combo Pack",    icon: "ri-gift-2-line",      color: "#C8A96E" },
};

const FILE_TYPE_INFO: Record<string, { icon: string; label: string }> = {
  video:    { icon: "ri-video-line",       label: "Video" },
  photo:    { icon: "ri-image-line",       label: "Photo" },
  download: { icon: "ri-download-line",    label: "Download" },
  cover:    { icon: "ri-image-edit-line",  label: "Cover" },
  other:    { icon: "ri-file-3-line",      label: "File" },
};

/* ─── Main ───────────────────────────────────────────────────── */
export default function MediaPage() {
  const { public_token } = useParams<{ public_token: string }>();
  const [loading, setLoading] = useState(true);
  const [pkg, setPkg] = useState<MediaPkg | null>(null);
  const [files, setFiles] = useState<PkgFile[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!public_token) { setLoading(false); return; }
      const { data } = await supabase
        .from("media_packages")
        .select("*")
        .eq("public_token", public_token)
        .eq("is_published", true)
        .maybeSingle();
      if (!data) { setLoading(false); return; }
      const pkgData = data as MediaPkg;

      // Increment view count
      await supabase.from("media_packages")
        .update({ view_count: (pkgData.view_count ?? 0) + 1 })
        .eq("id", pkgData.id);

      // Fetch multi-files
      const { data: fileRows } = await supabase
        .from("media_package_files")
        .select("id, file_type, title, url, thumbnail_url, sort_order")
        .eq("media_package_id", pkgData.id)
        .order("sort_order", { ascending: true });

      setPkg(pkgData);
      setFiles((fileRows ?? []) as PkgFile[]);
      setLoading(false);
    };
    load();
  }, [public_token]);

  const handleDownloadClick = async () => {
    if (!pkg) return;
    await supabase.from("media_packages")
      .update({ download_count: (pkg.download_count ?? 0) + 1 })
      .eq("id", pkg.id);
    setPkg((prev) => prev ? { ...prev, download_count: (prev.download_count ?? 0) + 1 } : prev);
  };

  if (loading) return <MediaLoading />;
  if (!pkg) return <MediaNotFound />;

  const typeInfo = TYPE_INFO[pkg.package_type] ?? { label: pkg.package_type, icon: "ri-disc-line", color: "#C8A96E" };
  const embedUrl = pkg.video_url ? getEmbedUrl(pkg.video_url) : null;
  const canEmbed = pkg.video_url ? isEmbeddable(pkg.video_url) : false;

  // Separate files by type for display
  const videoFiles = files.filter((f) => f.file_type === "video");
  const photoFiles = files.filter((f) => f.file_type === "photo" || f.file_type === "cover");
  const downloadFiles = files.filter((f) => f.file_type === "download" || f.file_type === "other");

  return (
    <div className="min-h-screen bg-[#0a0a0e] py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header card */}
        <div className="rounded-2xl overflow-hidden border border-white/8 bg-[#111114]">
          {pkg.cover_url && (
            <div className="h-48 w-full overflow-hidden relative">
              <img src={pkg.cover_url} alt={pkg.title} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111114] via-black/20 to-transparent" />
            </div>
          )}

          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: typeInfo.color + "20" }}>
                <i className={`${typeInfo.icon} text-xl`} style={{ color: typeInfo.color }}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: typeInfo.color + "20", color: typeInfo.color }}>
                    {typeInfo.label}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-400">Available</span>
                </div>
                <h1 className="text-white text-xl font-bold">{pkg.title}</h1>
                {pkg.usb_label && (
                  <p className="text-white/40 text-sm mt-1 flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-usb-line text-xs"></i></span>
                    {pkg.usb_label}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-[10px] text-white/25 flex items-center gap-1">
                    <i className="ri-eye-line"></i>{pkg.view_count ?? 0} views
                  </span>
                  <span className="text-[10px] text-white/25 flex items-center gap-1">
                    <i className="ri-download-line"></i>{pkg.download_count ?? 0} downloads
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legacy video_url */}
        {pkg.video_url && videoFiles.length === 0 && (
          <div className="rounded-2xl overflow-hidden border border-white/8 bg-[#111114]">
            <div className="p-4 border-b border-white/5 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-white/40">
                <i className="ri-play-circle-line text-sm"></i>
              </span>
              <p className="text-white/60 text-xs uppercase tracking-wider font-medium">Video</p>
            </div>
            {canEmbed && embedUrl ? (
              <div className="aspect-video w-full">
                <iframe src={embedUrl} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={pkg.title} />
              </div>
            ) : (
              <div className="p-5">
                <video controls className="w-full rounded-xl" src={pkg.video_url}>
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>
        )}

        {/* Multi-file videos */}
        {videoFiles.length > 0 && (
          <div className="space-y-4">
            {videoFiles.map((f) => {
              const fEmbed = getEmbedUrl(f.url);
              const fCanEmbed = isEmbeddable(f.url);
              return (
                <div key={f.id} className="rounded-2xl overflow-hidden border border-white/8 bg-[#111114]">
                  <div className="p-4 border-b border-white/5 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-white/40">
                      <i className="ri-video-line text-sm"></i>
                    </span>
                    <p className="text-white/60 text-xs uppercase tracking-wider font-medium">{f.title || "Video"}</p>
                  </div>
                  {fCanEmbed && fEmbed ? (
                    <div className="aspect-video w-full">
                      <iframe src={fEmbed} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={f.title} />
                    </div>
                  ) : (
                    <div className="p-5">
                      <video controls className="w-full rounded-xl" src={f.url}>
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Frame / Portada preview */}
        {pkg.frame_url && (
          <div className="rounded-2xl overflow-hidden border border-white/8 bg-[#111114]">
            <div className="p-4 border-b border-white/5 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-white/40">
                <i className="ri-image-edit-line text-sm"></i>
              </span>
              <p className="text-white/60 text-xs uppercase tracking-wider font-medium">Cover / Frame Preview</p>
            </div>
            <div className="p-5">
              <img src={pkg.frame_url} alt="Frame preview" className="w-full max-h-96 object-contain rounded-xl bg-white/3" />
            </div>
          </div>
        )}

        {/* Photo / Cover files grid */}
        {photoFiles.length > 0 && (
          <div className="rounded-2xl overflow-hidden border border-white/8 bg-[#111114]">
            <div className="p-4 border-b border-white/5 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-white/40">
                <i className="ri-image-line text-sm"></i>
              </span>
              <p className="text-white/60 text-xs uppercase tracking-wider font-medium">Photos</p>
            </div>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photoFiles.map((f) => (
                <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" className="group relative rounded-xl overflow-hidden bg-white/5 aspect-square">
                  <img src={f.thumbnail_url || f.url} alt={f.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  {f.title && (
                    <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-white text-[10px] font-medium truncate">{f.title}</p>
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Download files + legacy download_url */}
        {(pkg.download_url || downloadFiles.length > 0) && (
          <div className="rounded-2xl border border-white/8 bg-[#111114] p-6 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-4 h-4 flex items-center justify-center text-white/40">
                <i className="ri-download-2-line text-sm"></i>
              </span>
              <p className="text-white/60 text-xs uppercase tracking-wider font-medium">Downloads</p>
            </div>

            {/* Legacy download_url */}
            {pkg.download_url && (
              <a href={pkg.download_url} target="_blank" rel="noopener noreferrer" onClick={handleDownloadClick}
                className="flex items-center gap-4 rounded-xl bg-white/5 hover:bg-white/8 border border-white/5 p-4 transition-colors cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C8A96E]/15 text-[#C8A96E]">
                  <i className="ri-download-2-line text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">Download Your Files</p>
                  <p className="text-white/30 text-xs mt-0.5 truncate">{pkg.download_url}</p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/15 text-[#C8A96E]">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
            )}

            {/* Multi-file downloads */}
            {downloadFiles.map((f) => (
              <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" onClick={handleDownloadClick}
                className="flex items-center gap-4 rounded-xl bg-white/5 hover:bg-white/8 border border-white/5 p-4 transition-colors cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C8A96E]/15 text-[#C8A96E]">
                  <i className="ri-file-3-line text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">{f.title || "File"}</p>
                  <p className="text-white/30 text-xs mt-0.5 truncate">{f.url}</p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/15 text-[#C8A96E]">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Notes */}
        {pkg.notes && (
          <div className="rounded-2xl border border-white/8 bg-[#111114] p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-4 h-4 flex items-center justify-center text-white/40">
                <i className="ri-file-list-3-line text-sm"></i>
              </span>
              <p className="text-white/60 text-xs uppercase tracking-wider font-medium">Notes</p>
            </div>
            <p className="text-white/50 text-sm leading-relaxed">{pkg.notes}</p>
          </div>
        )}

        {/* Powered by */}
        <p className="text-center text-xs text-white/15 pb-4">
          Delivered by{" "}
          <span className="text-white/30 font-semibold tracking-wider">Pixory AI</span>
          {" "}· Professional Media Packages
        </p>
      </div>
    </div>
  );
}
